/**
 * Prompts Configuration Page
 * Admin page for managing LLM agent prompts
 * Route: /configuration/prompts
 */
import { useState, useMemo } from 'react'
import { PageShell } from '@/components/common/PageShell'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState } from '@/components/common/EmptyState'
import { usePermissions } from '@/hooks/usePermissions'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Plus, Search, FileText } from 'lucide-react'
import { usePrompts, usePromptDetail } from '@/api/prompts'
import { AGENT_OPTIONS } from '@/types/prompts'
import { PromptStatusBadge } from '@/components/prompts/PromptStatusBadge'
import { PromptDetailDialog } from '@/components/prompts/PromptDetailDialog'
import { formatDistanceToNow } from 'date-fns'

/**
 * Loading skeleton for the prompts table
 */
function PromptsLoadingSkeleton() {
  return (
    <div data-testid="prompts-loading" className="space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  )
}

/**
 * Format relative time for display
 */
function formatRelativeTime(dateString: string): string {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true })
  } catch {
    return dateString
  }
}

export function PromptsPage() {
  const { role, isLoading: permissionsLoading } = usePermissions()
  // PromptAdmin policy requires SuperAdmin role
  const canAccessPromptAdmin = role === 'super_admin'
  const { data, isLoading } = usePrompts()

  // Local state for filtering
  const [searchTerm, setSearchTerm] = useState('')
  const [agentFilter, setAgentFilter] = useState<string>('all')

  // Selected prompt for detail sheet
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  // Fetch detail for selected prompt
  const { data: promptDetail, isLoading: detailLoading } = usePromptDetail(selectedPrompt)

  // Show loading while checking permissions
  if (permissionsLoading) {
    return (
      <PageShell>
        <div className="p-6 space-y-6">
          <PageHeader
            title="Prompt Management"
            description="Manage LLM agent prompts with versioning and A/B testing"
          />
          <PromptsLoadingSkeleton />
        </div>
      </PageShell>
    )
  }

  // Check permissions
  if (!canAccessPromptAdmin) {
    return (
      <PageShell>
        <div className="p-6 space-y-6">
          <PageHeader
            title="Prompt Management"
            description="Manage LLM agent prompts with versioning and A/B testing"
          />
          <EmptyState
            icon={FileText}
            title="Access Denied"
            description="You do not have permission to manage prompts. Contact an administrator for access."
          />
        </div>
      </PageShell>
    )
  }

  // Filter prompts based on search and agent
  const filteredPrompts = useMemo(() => {
    if (!data?.prompts) return []

    return data.prompts.filter((prompt) => {
      const matchesSearch =
        searchTerm === '' ||
        prompt.promptName.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesAgent =
        agentFilter === 'all' || prompt.agent === agentFilter

      return matchesSearch && matchesAgent
    })
  }, [data?.prompts, searchTerm, agentFilter])

  // Handle row click to open detail sheet
  const handleRowClick = (promptName: string) => {
    setSelectedPrompt(promptName)
    setSheetOpen(true)
  }

  // Handle sheet close
  const handleSheetClose = (open: boolean) => {
    setSheetOpen(open)
    if (!open) {
      setSelectedPrompt(null)
    }
  }

  // Render empty state
  if (!isLoading && (!data?.prompts || data.prompts.length === 0)) {
    return (
      <PageShell>
        <div className="p-6 space-y-6">
          <PageHeader
            title="Prompt Management"
            description="Manage LLM agent prompts with versioning and A/B testing"
          />
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add Prompt
          </Button>
          <EmptyState
            icon={FileText}
            title="No prompts configured"
            description="Import prompts from embedded resources to get started"
            action={
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Your First Prompt
              </Button>
            }
          />
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        <PageHeader
          title="Prompt Management"
          description="Manage LLM agent prompts with versioning and A/B testing"
        />

        {/* Actions bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search prompts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64 pl-9"
              />
            </div>

            {/* Agent filter */}
            <Select value={agentFilter} onValueChange={setAgentFilter}>
              <SelectTrigger className="w-40" aria-label="Agent">
                <SelectValue placeholder="All Agents" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Agents</SelectItem>
                {AGENT_OPTIONS.map((agent) => (
                  <SelectItem key={agent.value} value={agent.value}>
                    {agent.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add Prompt
          </Button>
        </div>

        {/* Table */}
        <div>
          {isLoading ? (
            <PromptsLoadingSkeleton />
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPrompts.map((prompt) => (
                    <TableRow
                      key={prompt.promptName}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleRowClick(prompt.promptName)}
                    >
                      <TableCell className="font-medium">
                        {prompt.promptName}
                      </TableCell>
                      <TableCell>{prompt.agent}</TableCell>
                      <TableCell>v{prompt.version}</TableCell>
                      <TableCell>
                        <PromptStatusBadge status={prompt.status} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatRelativeTime(prompt.updatedAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Pagination info */}
        {!isLoading && filteredPrompts.length > 0 && (
          <div className="text-sm text-muted-foreground">
            Showing {filteredPrompts.length} of {data?.total ?? 0} prompts
          </div>
        )}

        {/* Detail Dialog */}
        <PromptDetailDialog
          promptName={selectedPrompt}
          promptDetail={promptDetail ?? null}
          isLoading={detailLoading}
          open={sheetOpen}
          onOpenChange={handleSheetClose}
        />
      </div>
    </PageShell>
  )
}
